import { z } from 'zod';

/**
 * Änderungsantrag am Artikelstamm (VB-26016, ADR 0089).
 *
 * Die Technik darf Artikel bearbeiten, ihre Änderung wird aber erst nach
 * Freigabe durch das Sekretariat wirksam — dasselbe Vier-Augen-Prinzip wie beim
 * Artikelnummern-Antrag, und dieselbe Warteschlange (Technik → Anträge).
 *
 * Ein Antrag trägt genau EINE Änderung. Wer drei Felder ändert, stellt einen
 * Antrag mit drei Feldern (`FIELDS`); wer eine Zusatzmaterial-Zeile ändert,
 * einen eigenen. So bleibt die Freigabe eine Ja/Nein-Entscheidung und muss nicht
 * teilweise angenommen werden können.
 */

/** Welche Art von Änderung beantragt wird. */
export const ARTICLE_CHANGE_KINDS = ['FIELDS', 'KIT_ADD', 'KIT_UPDATE', 'KIT_DELETE'] as const;
export type ArticleChangeKind = (typeof ARTICLE_CHANGE_KINDS)[number];

/**
 * Artikelfelder, die per Antrag geändert werden dürfen. Bewusst eine
 * Positivliste — nur technische Angaben. Draußen bleiben:
 * - Preise, Bestand, Sperrkennzeichen (kaufmännisch bzw. buchend),
 * - Melde- und Höchstbestand: das ist Disposition und damit Sache des Einkaufs
 *   (Entscheid Tech-Lead 2026-08-11).
 */
export const articleChangeFieldsSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    nameEn: z.string().trim().max(200).nullable(),
    description: z.string().trim().max(4000).nullable(),
    descriptionEn: z.string().trim().max(4000).nullable(),
    internalNotes: z.string().trim().max(4000).nullable(),
    unit: z.string().trim().max(30),
    weightKg: z.number().min(0).nullable(),
    taricCode: z.string().trim().max(30).nullable(),
    countryOfOrigin: z.string().trim().max(2).nullable(),
    manufacturer: z.string().trim().max(200).nullable(),
    ean: z.string().trim().max(30).nullable(),
    matchcode: z.string().trim().max(100).nullable(),
    altCode: z.string().trim().max(100).nullable(),
    commodityGroup: z.number().int().min(1000).max(9999).nullable(),
  })
  .partial();
export type ArticleChangeFields = z.infer<typeof articleChangeFieldsSchema>;

/** Menschenlesbare Beschriftungen der änderbaren Felder (für den Vergleich). */
export const ARTICLE_CHANGE_FIELD_LABELS: Record<keyof ArticleChangeFields, string> = {
  name: 'Bezeichnung',
  nameEn: 'Bezeichnung (EN)',
  description: 'Infotext',
  descriptionEn: 'Infotext (EN)',
  internalNotes: 'Interne Notizen',
  unit: 'Einheit',
  weightKg: 'Gewicht (kg)',
  taricCode: 'Zolltarifnummer',
  countryOfOrigin: 'Ursprungsland',
  manufacturer: 'Hersteller',
  ean: 'EAN',
  matchcode: 'Matchcode',
  altCode: 'Alt-Artikelnummer',
  commodityGroup: 'Warengruppe',
};

export const articleChangePayloadSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('FIELDS'),
    /** Nur die geänderten Felder — leer wäre ein Antrag ohne Inhalt. */
    fields: articleChangeFieldsSchema.refine((value) => Object.keys(value).length > 0, {
      message: 'Der Antrag enthält keine Änderung.',
    }),
  }),
  z.object({
    kind: z.literal('KIT_ADD'),
    /** Artikel, der als Zusatzmaterial dazukommen soll. */
    kitArticleId: z.string().uuid(),
    quantityPerUnit: z.number().positive(),
    note: z.string().trim().max(500).nullish(),
  }),
  z.object({
    kind: z.literal('KIT_UPDATE'),
    kitItemId: z.string().uuid(),
    quantityPerUnit: z.number().positive().optional(),
    note: z.string().trim().max(500).nullish(),
  }),
  z.object({
    kind: z.literal('KIT_DELETE'),
    kitItemId: z.string().uuid(),
  }),
]);
export type ArticleChangePayload = z.infer<typeof articleChangePayloadSchema>;

export const createArticleChangeRequestSchema = z.object({
  articleId: z.string().uuid(),
  payload: articleChangePayloadSchema,
});
export type CreateArticleChangeRequestInput = z.infer<typeof createArticleChangeRequestSchema>;

export const rejectArticleChangeRequestSchema = z.object({
  reason: z.string().trim().min(1, 'Bitte einen Grund angeben.').max(1000),
});
export type RejectArticleChangeRequestInput = z.infer<typeof rejectArticleChangeRequestSchema>;

/** Eine Zeile des Vorher/Nachher-Vergleichs. */
export const articleChangeDiffLineSchema = z.object({
  label: z.string(),
  before: z.string(),
  after: z.string(),
});
export type ArticleChangeDiffLine = z.infer<typeof articleChangeDiffLineSchema>;

export const articleChangeRequestResponseSchema = z.object({
  id: z.string().uuid(),
  articleId: z.string().uuid(),
  articleNumber: z.string(),
  articleName: z.string(),
  status: z.enum(['PENDING', 'APPROVED', 'REJECTED', 'RETURNED', 'WITHDRAWN']),
  kind: z.enum(ARTICLE_CHANGE_KINDS),
  /** Der Vergleich, fertig aufbereitet — die Freigabe muss nichts nachrechnen. */
  diff: z.array(articleChangeDiffLineSchema),
  /**
   * Antragsteller/Freigeber als ID — die Namen löst das Frontend über die
   * Benutzerliste auf, wie beim Artikelnummern-Antrag auch.
   */
  requestedById: z.string().uuid(),
  reviewedById: z.string().uuid().nullable(),
  reviewedAt: z.string().datetime().nullable(),
  rejectionReason: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ArticleChangeRequestResponse = z.infer<typeof articleChangeRequestResponseSchema>;

/** Kurzbezeichnung der Antragsart für Listen und Meldungen. */
export const ARTICLE_CHANGE_KIND_LABELS: Record<ArticleChangeKind, string> = {
  FIELDS: 'Artikeldaten',
  KIT_ADD: 'Zusatzmaterial ergänzen',
  KIT_UPDATE: 'Zusatzmaterial ändern',
  KIT_DELETE: 'Zusatzmaterial entfernen',
};

/** Leerwert-Anzeige im Vergleich — „—" statt einer leeren Zelle. */
const DASH = '—';

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return DASH;
  if (typeof value === 'boolean') return value ? 'ja' : 'nein';
  if (typeof value === 'number') return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 }).format(value);
  return String(value);
}

/**
 * Vorher/Nachher eines Antrags — rein und testbar, damit Liste, Freigabe-Dialog
 * und Protokoll dieselbe Darstellung zeigen.
 *
 * Felder, die sich nicht unterscheiden, fallen heraus: Ein Antrag, der ein Feld
 * auf denselben Wert setzt, ist keine Änderung und soll die Freigabe nicht mit
 * Rauschen belasten.
 */
export function articleChangeDiff(
  payload: ArticleChangePayload,
  before: Record<string, unknown>,
): ArticleChangeDiffLine[] {
  if (payload.kind === 'FIELDS') {
    return Object.entries(payload.fields)
      .filter(([field, after]) => formatValue(before[field]) !== formatValue(after))
      .map(([field, after]) => ({
        label: ARTICLE_CHANGE_FIELD_LABELS[field as keyof ArticleChangeFields] ?? field,
        before: formatValue(before[field]),
        after: formatValue(after),
      }));
  }
  if (payload.kind === 'KIT_ADD') {
    return [
      {
        label: 'Zusatzmaterial',
        before: DASH,
        after: `${formatValue(before.kitArticleNumber)} · ${formatValue(before.kitArticleName)} — ${formatValue(payload.quantityPerUnit)} je Einheit`,
      },
    ];
  }
  if (payload.kind === 'KIT_UPDATE') {
    const lines: ArticleChangeDiffLine[] = [];
    const label = `Zusatzmaterial ${formatValue(before.kitArticleNumber)}`;
    if (payload.quantityPerUnit !== undefined) {
      lines.push({
        label: `${label} — Stückzahl je Einheit`,
        before: formatValue(before.quantityPerUnit),
        after: formatValue(payload.quantityPerUnit),
      });
    }
    if (payload.note !== undefined) {
      lines.push({ label: `${label} — Notiz`, before: formatValue(before.note), after: formatValue(payload.note) });
    }
    return lines;
  }
  return [
    {
      label: 'Zusatzmaterial entfernen',
      before: `${formatValue(before.kitArticleNumber)} · ${formatValue(before.kitArticleName)} — ${formatValue(before.quantityPerUnit)} je Einheit`,
      after: DASH,
    },
  ];
}
